import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import ModeToggle from '../components/ModeToggle';
import { Sparkles } from 'lucide-react';

const SUBJECTS = ['Math', 'Reading', 'Writing', 'Science'];
const MATH_TOPICS = ['Algebra', 'Basic Precalculus', 'Trigonometry', 'Percentages', 'Basic Statistics'];

export default function QuestionBank() {
  const { API, mode } = useApp();
  const nav = useNavigate();
  const isCyber = mode === 'cyber';
  const [subject, setSubject] = useState('Math');
  const [topic, setTopic] = useState('');
  const [qs, setQs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reveal, setReveal] = useState({});

  const load = async () => {
    setLoading(true);
    const params = { mode, subject, limit: 20 };
    if (topic) params.topic = topic;
    const r = await axios.get(`${API}/questions`, { params });
    setQs(r.data.questions);
    setLoading(false);
  };

  useEffect(() => { setTopic(''); }, [subject]);
  useEffect(() => { load(); }, [subject, topic, mode]);

  const generateMore = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/questions/generate-more`, { mode, subject, topic: topic || null, count: 5 });
      await load();
    } finally { setLoading(false); }
  };

  return (
    <div className={`min-h-screen px-6 py-10 ${isCyber ? 'bg-black text-white' : 'text-[#2C2621]'}`}>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <button data-testid="bank-back" onClick={() => nav('/')} className={isCyber ? 'font-mono-cyber text-[#00FFD1]' : 'font-body-story'}>← back</button>
          <ModeToggle />
        </div>
        <h1 className={isCyber ? 'font-orbitron text-3xl mb-6' : 'font-storybook text-4xl mb-6'}>
          {isCyber ? 'QUESTION BANK' : 'The Royal Library'}
        </h1>

        <div className="flex flex-wrap gap-2 mb-4">
          {SUBJECTS.map(s => (
            <button key={s} data-testid={`bank-subject-${s}`} onClick={() => setSubject(s)}
              className={`px-4 py-2 rounded-lg text-sm ${
                subject === s
                  ? isCyber ? 'bg-[#00FFD1] text-black font-orbitron' : 'bg-[#2B5329] text-white font-storybook'
                  : isCyber ? 'border border-neutral-700 font-mono-cyber' : 'border-2 border-[#C9B99A] font-body-story'
              }`}>{s}</button>
          ))}
        </div>

        {subject === 'Math' && (
          <div className="flex flex-wrap gap-2 mb-6">
            <button onClick={() => setTopic('')} data-testid="bank-topic-all"
              className={`px-3 py-1.5 rounded text-xs ${topic === '' ? (isCyber ? 'bg-[#00FFD1]/20 text-[#00FFD1]' : 'bg-[#8C1C13]/15 text-[#8C1C13]') : 'opacity-70'}`}>All Topics</button>
            {MATH_TOPICS.map(t => (
              <button key={t} onClick={() => setTopic(t)} data-testid={`bank-topic-${t.replace(/ /g,'-')}`}
                className={`px-3 py-1.5 rounded text-xs ${topic === t ? (isCyber ? 'bg-[#00FFD1]/20 text-[#00FFD1]' : 'bg-[#8C1C13]/15 text-[#8C1C13]') : 'opacity-70'}`}>{t}</button>
            ))}
          </div>
        )}

        <div className="mb-6 flex items-center gap-3">
          <button data-testid="bank-generate-more" onClick={generateMore} disabled={loading}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${isCyber ? 'bg-[#00FFD1] text-black font-orbitron' : 'storybook-btn'}`}>
            <Sparkles size={14} /> {loading ? 'Working…' : 'Generate more with AI'}
          </button>
          <span className="text-sm opacity-70">{qs.length} questions loaded</span>
        </div>

        <div className="space-y-3">
          {qs.map((q, i) => (
            <motion.div key={q.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              className={isCyber ? 'glass-cyber rounded-xl p-5' : 'parchment rounded-xl p-5'}>
              <div className={isCyber ? 'font-mono-cyber text-[#00FFD1] text-xs mb-2' : 'font-body-story italic text-[#68615A] mb-2'}>
                {q.subject} · {q.topic} · difficulty {q.difficulty}
              </div>
              <div className={`text-lg mb-3 ${isCyber ? 'font-orbitron' : 'font-storybook'}`}>{q.prompt}</div>
              <div className="grid sm:grid-cols-2 gap-2 mb-3">
                {Object.entries(q.choices || {}).map(([l, t]) => (
                  <div key={l} className={`px-3 py-2 rounded ${isCyber ? 'bg-black/40 border border-neutral-800' : 'bg-white border border-[#C9B99A]'}`}>
                    <span className={`inline-block w-6 font-bold ${isCyber ? 'text-[#00FFD1]' : 'text-[#8C1C13]'}`}>{l}.</span>{t}
                  </div>
                ))}
              </div>
              <button data-testid={`bank-reveal-${q.id}`} onClick={() => setReveal(r => ({ ...r, [q.id]: !r[q.id] }))}
                className={`text-xs ${isCyber ? 'text-[#00FFD1] font-mono-cyber' : 'text-[#8C1C13] font-body-story italic'}`}>
                {reveal[q.id] ? `Answer: ${q.answer}` : 'Reveal answer'}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
